import { QUOTAS } from '@xb/shared';
import { type FormEvent, useId, useState } from 'react';
import { api } from '../api.js';
import { useAction, useResource } from '../hooks.js';
import { useApp } from '../pages/AppPage.js';
import { ErrorNotice, Loading, Notice } from './Notice.js';

const KEY = /^[A-Z_][A-Z0-9_]{0,63}$/;

interface Row {
  id: number;
  key: string;
  value: string;
}

let nextRowId = 1;
const newRow = (): Row => ({ id: nextRowId++, key: '', value: '' });

export function EnvironmentTab() {
  const { app, reload: reloadApp } = useApp();
  const env = useResource(() => api.getEnv(app.slug), [app.slug]);
  const [rows, setRows] = useState<Row[]>([newRow()]);
  const [saved, setSaved] = useState<string[] | undefined>(undefined);
  const formId = useId();

  const filled = rows.filter((r) => r.key.trim() !== '' || r.value !== '');
  const problems = filled
    .map((r) => {
      const k = r.key.trim();
      if (!KEY.test(k)) return `${k || '(blank)'}: use UPPER_SNAKE_CASE, letters, digits and underscores`;
      if (env.data?.platformKeys.includes(k) || k.startsWith('XB_'))
        return `${k} is set by the platform and can't be changed`;
      return undefined;
    })
    .filter((p): p is string => !!p);
  const dupes = new Set(filled.map((r) => r.key.trim()).filter((k, i, all) => all.indexOf(k) !== i));
  if (dupes.size) problems.push(`Repeated: ${[...dupes].join(', ')}`);

  const save = useAction(async (e: FormEvent) => {
    e.preventDefault();
    if (filled.length === 0 || problems.length > 0) return;
    const vars: Record<string, string> = {};
    for (const r of filled) vars[r.key.trim()] = r.value;
    const res = await api.putEnv(app.slug, vars);
    env.set({ keys: res.keys, platformKeys: env.data?.platformKeys ?? [] });
    setSaved(Object.keys(vars));
    setRows([newRow()]);
    reloadApp();
  });

  const [removing, setRemoving] = useState<string | undefined>(undefined);
  const remove = useAction(async (key: string) => {
    setRemoving(key);
    try {
      const res = await api.putEnv(app.slug, { [key]: null });
      env.set({ keys: res.keys, platformKeys: env.data?.platformKeys ?? [] });
      reloadApp();
    } finally {
      setRemoving(undefined);
    }
  });

  const total =
    (env.data?.keys.length ?? 0) + filled.filter((r) => !env.data?.keys.includes(r.key.trim())).length;

  return (
    <>
      <Notice kind="info">
        Values are <strong>write-only</strong>: once saved they're never shown again, here or anywhere else.
        Keep your own copy somewhere safe. To change a value, set it again.
      </Notice>

      <section className="card">
        <h2>Set variables</h2>
        <p className="small subtle">
          Your app reads these as environment variables. This is where your own Xero API client ID and secret
          go. Changes reach the running app within a minute.
        </p>
        <form onSubmit={(e) => void save.run(e)} noValidate aria-describedby={`${formId}-hint`}>
          {rows.map((r, i) => (
            <div className="env-row" key={r.id}>
              <div>
                <label className="visually-hidden" htmlFor={`${formId}-k-${r.id}`}>
                  Name {i + 1}
                </label>
                <input
                  id={`${formId}-k-${r.id}`}
                  type="text"
                  className="mono"
                  placeholder="XERO_CLIENT_ID"
                  value={r.key}
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => {
                    setSaved(undefined);
                    setRows(
                      rows.map((x) => (x.id === r.id ? { ...x, key: e.target.value.toUpperCase() } : x)),
                    );
                  }}
                />
              </div>
              <div>
                <label className="visually-hidden" htmlFor={`${formId}-v-${r.id}`}>
                  Value {i + 1}
                </label>
                <input
                  id={`${formId}-v-${r.id}`}
                  type="text"
                  className="mono"
                  placeholder="value"
                  value={r.value}
                  autoComplete="off"
                  spellCheck={false}
                  maxLength={4096}
                  onChange={(e) => {
                    setSaved(undefined);
                    setRows(rows.map((x) => (x.id === r.id ? { ...x, value: e.target.value } : x)));
                  }}
                />
              </div>
              <button
                type="button"
                className="btn btn-sm"
                aria-label={`Remove row ${i + 1}`}
                disabled={rows.length === 1}
                onClick={() => setRows(rows.filter((x) => x.id !== r.id))}
              >
                ✕
              </button>
            </div>
          ))}
          <div className="btn-row" style={{ marginBottom: '0.75rem' }}>
            <button type="button" className="btn btn-sm" onClick={() => setRows([...rows, newRow()])}>
              Add another
            </button>
          </div>
          {problems.length > 0 ? (
            <ul className="notice notice-error" role="alert" style={{ paddingLeft: '1.75rem' }}>
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          ) : null}
          {total > QUOTAS.envVarsPerApp ? (
            <Notice kind="warn">At most {QUOTAS.envVarsPerApp} variables per app.</Notice>
          ) : null}
          <ErrorNotice error={save.error} />
          {saved ? <Notice kind="ok">Saved {saved.join(', ')}.</Notice> : null}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={
              save.pending || filled.length === 0 || problems.length > 0 || total > QUOTAS.envVarsPerApp
            }
          >
            {save.pending ? 'Saving…' : 'Save'}
          </button>
          <p className="hint" id={`${formId}-hint`} style={{ marginTop: '0.75rem' }}>
            Saving a name that already exists replaces its value.
          </p>
        </form>
      </section>

      <section className="card">
        <h2>Your variables</h2>
        <ErrorNotice error={env.error ?? remove.error} />
        {env.loading && !env.data ? <Loading /> : null}
        {env.data && env.data.keys.length === 0 ? <p className="subtle">None set yet.</p> : null}
        {env.data && env.data.keys.length > 0 ? (
          <ul className="rows">
            {env.data.keys.map((k) => (
              <li key={k}>
                <div>
                  <span className="primary mono">{k}</span> <span className="secondary">= (hidden)</span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-danger"
                  disabled={remove.pending}
                  onClick={() => void remove.run(k)}
                  aria-label={`Delete ${k}`}
                >
                  {removing === k ? 'Deleting…' : 'Delete'}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      {env.data ? (
        <section className="card">
          <h2>Set by the platform</h2>
          <p className="small subtle">
            Your app gets these automatically. You can read them but not change them; see docs/HOSTING.md for
            what each one is for.
          </p>
          <ul className="rows">
            {env.data.platformKeys.map((k) => (
              <li key={k}>
                <span className="primary mono">{k}</span>
                <span className="secondary">read-only</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}
